import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchExecutiveCommandCenterDashboard,
  fetchExecutiveCommandCenterRecommendations,
} from '../lib/executiveCommandCenterApi';
import { executiveCommandCenterQueryKeys } from '../lib/executiveCommandCenterQueryKeys';

export const useExecutiveCommandCenterData = () => {
  const [timeframe, setTimeframe] = useState('today');
  const [areaFilter, setAreaFilter] = useState('all');

  const dashboardQuery = useQuery({
    queryKey: executiveCommandCenterQueryKeys.dashboard(timeframe),
    queryFn: () => fetchExecutiveCommandCenterDashboard(timeframe),
    refetchInterval: 15000,
  });

  const recommendationQuery = useQuery({
    queryKey: executiveCommandCenterQueryKeys.recommendations(timeframe),
    queryFn: () => fetchExecutiveCommandCenterRecommendations(timeframe),
    refetchInterval: 15000,
  });

  const dashboard = dashboardQuery.data || {};
  const areaRiskRows = useMemo(
    () => (Array.isArray(dashboard?.area_risk) ? dashboard.area_risk : []),
    [dashboard?.area_risk],
  );

  const filteredAreaRisk = useMemo(() => {
    if (!areaFilter || areaFilter === 'all') return areaRiskRows;
    return areaRiskRows.filter((row) => String(row?.area_name || '').toLowerCase() === String(areaFilter).toLowerCase());
  }, [areaFilter, areaRiskRows]);

  const recommendations = recommendationQuery.data?.items || dashboard?.strategic_recommendations || [];

  return {
    timeframe,
    setTimeframe,
    areaFilter,
    setAreaFilter,
    dashboard,
    recommendations,
    filteredAreaRisk,
    isLoading: dashboardQuery.isLoading || recommendationQuery.isLoading,
    error: dashboardQuery.error || recommendationQuery.error,
    refetch: () => {
      dashboardQuery.refetch();
      recommendationQuery.refetch();
    },
  };
};

export const useExecutiveCommandCenterDashboard = (timeframe = 'today') =>
  useQuery({
    queryKey: executiveCommandCenterQueryKeys.dashboard(timeframe),
    queryFn: () => fetchExecutiveCommandCenterDashboard(timeframe),
    refetchInterval: 15000,
  });

export const useExecutiveCommandCenterRecommendations = (timeframe = 'today') =>
  useQuery({
    queryKey: executiveCommandCenterQueryKeys.recommendations(timeframe),
    queryFn: () => fetchExecutiveCommandCenterRecommendations(timeframe),
    refetchInterval: 15000,
  });
